import {IFeatureLayerInfo, IQuery, ITableProps} from '../interfaces/models'
import {IFieldInfo} from '../interfaces/reducers'
import {Feature, FeatureStatus} from '../models'
import {format} from 'date-fns'

export const ensureQuery = (tableProps: ITableProps, layerInfo: IFeatureLayerInfo): IQuery => {
  let query: IQuery = {
    where: layerInfo.sourceDescription.constantSqlWhere,
    returnGeometry: true
  }

  if (tableProps.query) {
    query = {...tableProps.query}
  }
  if (tableProps.groupField) {
    query.outFields = [tableProps.groupField]
    query.returnDistinctValues = true
    query.returnGeometry = false
    query.orderByFields = [tableProps.groupField]
  }
  if (!query.outFields) {
    query.outFields = getMinimalOutFields(tableProps, layerInfo)
  }
  if (!query.orderByFields && tableProps.idColumnName) {
    query.orderByFields = [`${tableProps.idColumnName} ASC`]
  }
  return query
}

export const getMinimalOutFields = (tableProps: ITableProps, layerInfo: IFeatureLayerInfo): Array<string> => {
  let outFieldsMinimal = [tableProps.idColumnName]

  tableProps.columns!.forEach(c => {
    outFieldsMinimal.push(c.id as string)
  })
  const filteredOutFieldsMinimal: Array<string> = []
  if (layerInfo.sourceDescription.tableFields) {
    outFieldsMinimal = outFieldsMinimal.concat(layerInfo.sourceDescription.tableFields)
  }
  for (const index in outFieldsMinimal) {
    if (layerInfo.fields[outFieldsMinimal[index]] && filteredOutFieldsMinimal.indexOf(outFieldsMinimal[index]) == -1) {
      filteredOutFieldsMinimal.push(outFieldsMinimal[index])
    }
  }

  return filteredOutFieldsMinimal
}

const getfieldNomenclatureValue = (fieldInfo: IFieldInfo, attributeValue: string | number | undefined): string => {
  const nomenclatureValue = fieldInfo.codedValues!.find(
    c =>
      c.code != undefined &&
      c.code != null &&
      attributeValue != undefined &&
      attributeValue != null &&
      c.code.toString() == attributeValue.toString()
  )
  return nomenclatureValue ? nomenclatureValue.name : ''
}

const getEnumMultiValue = (fieldInfo: IFieldInfo, attributeValue: string | number | undefined): string => {
  const nomenclatureValue = fieldInfo
    .codedValues!.filter(
      c =>
        c.code != undefined &&
        c.code != null &&
        attributeValue != undefined &&
        attributeValue != null &&
        ((c.code as number) & (attributeValue as number)) == (c.code as number)
    )
    .map(c => c.name)
    .join(', ')
  return nomenclatureValue ? nomenclatureValue : ''
}

export const getDateString = (date: Date) => {
  return format(date, 'dd.MM.yyyy')
}

const getFieldStringValue = (fieldInfo: IFieldInfo, attributeValue: string | number | undefined): string => {
  const value = attributeValue

  return value != undefined && value != null ? value.toString() : ''
}

const getFieldIntValue = (fieldInfo: IFieldInfo, attributeValue: string | number | undefined): number | string => {
  if (attributeValue === null || attributeValue === undefined) {
    return ''
  } else {
    if (typeof attributeValue == 'number' || !isNaN(parseInt(attributeValue))) {
      return parseInt(attributeValue as string)
    } else {
      throw new Error(`${fieldInfo.fieldName} is marked as integer but the value it holds is ${typeof attributeValue}`)
    }
  }
}

const getFieldFloatValue = (fieldInfo: IFieldInfo, attributeValue: string | number | undefined): number | string => {
  if (attributeValue === null || attributeValue === undefined) {
    return ''
  } else {
    if (typeof attributeValue == 'number' || !isNaN(parseFloat(attributeValue))) {
      return parseFloat(attributeValue as string)
    } else {
      throw new Error(`${fieldInfo.fieldName} is marked as integer but the value it holds is ${typeof attributeValue}`)
    }
  }
}

export const getFieldValue = (fieldInfo: IFieldInfo, attributeValue: string | number | undefined): string | number => {
  if (fieldInfo.codedValues && fieldInfo.type == 'enum') {
    return getEnumMultiValue(fieldInfo, attributeValue)
  } else if (fieldInfo.codedValues) {
    return getfieldNomenclatureValue(fieldInfo, attributeValue)
  } else if (fieldInfo.type == 'integer') {
    return getFieldIntValue(fieldInfo, attributeValue)
  } else if (fieldInfo.type == 'double') {
    return getFieldFloatValue(fieldInfo, attributeValue)
  } else if (fieldInfo.type == 'date' && !!attributeValue) {
    return getDateString(new Date(attributeValue))
  } else {
    return getFieldStringValue(fieldInfo, attributeValue)
  }
}

export const createNewFeature = (layerInfo: IFeatureLayerInfo): Feature => {
  const feature: Feature = new Feature()
  for (const key in layerInfo.fields) {
    const fieldInfo = layerInfo.fields[key]
    if (fieldInfo.nullable) {
      feature.set(key, undefined)
    } else {
      if (fieldInfo.codedValues && fieldInfo.codedValues.length > 0) {
        feature.set(key, fieldInfo.codedValues[0].code)
      } else if (fieldInfo.type == 'date') {
        feature.set(key, new Date().getTime())
      } else if (fieldInfo.type == 'string') {
        feature.set(key, '')
      } else if (fieldInfo.type == 'integer' || fieldInfo.type == 'double' || fieldInfo.type == 'long') {
        feature.set(key, undefined)
      } else if (fieldInfo.type == 'oid') {
        feature.set(key, undefined)
      } else {
        feature.set(key, undefined)
        console.warn(`Cant set default value for type ${fieldInfo.type}`)
      }
    }
  }
  feature.status = FeatureStatus.New
  return feature
}

export const getFeatureTitle = (feature: Feature, layerInfo: IFeatureLayerInfo): string => {
  let title = layerInfo.sourceDescription.featureTitle
  const reg = /{([A-z]+)}/g
  const matches = title.match(reg)
  if (matches) {
    matches.forEach(m => {
      const fieldInfo = layerInfo.fields[m.replace('}', '').replace('{', '')]
      let value: React.ReactText = ''
      const atts = feature.getProperties()
      if (fieldInfo.codedValues) {
        const v = fieldInfo.codedValues.find(c => c.code == atts[fieldInfo.fieldName])
        if (v != undefined && v != null) {
          value = v.name
        }
      } else {
        value =
          atts[fieldInfo.fieldName] == undefined || atts[fieldInfo.fieldName] == null ? '' : atts[fieldInfo.fieldName]
      }
      title = title.replace(m, value.toString())
    })
  }
  return title
}

export const parseFeatureFieldsAll = (
  feature: Feature,
  layerInfo: IFeatureLayerInfo,
  preserveNulls?: boolean,
  customFieldParsers?: {
    [fieldName: string]: (fieldInfo: IFieldInfo, attributeValue: string | number | undefined) => string
  },
  customTypeParsers?: {
    [fieldName: string]: (fieldInfo: IFieldInfo, attributeValue: string | number | undefined) => string
  },
  skip?: {[fieldName: string]: any}
): {[label: string]: string} => {
  const fields: {[attLabel: string]: string} = {}
  let fieldInfo: IFieldInfo
  const atts = feature.getProperties()
  for (const fieldName in atts) {
    if (layerInfo.fields[fieldName]) {
      fieldInfo = layerInfo.fields[fieldName]

      if (!preserveNulls && (atts[fieldName] === '' || atts[fieldName] === null || atts[fieldName] === undefined)) {
        continue
      } else if (skip && skip[fieldName]) {
        continue
      } else {
        if (customFieldParsers && customFieldParsers[fieldInfo.fieldName]) {
          // fields override types
          fields[fieldInfo.fieldName] = customFieldParsers[fieldInfo.fieldName](fieldInfo, atts[fieldName])
        } else if (customTypeParsers && customTypeParsers[fieldInfo.type as any]) {
          fields[fieldInfo.fieldName] = customTypeParsers[fieldInfo.type as any](fieldInfo, atts[fieldName])
        } else {
          const val = getFieldValue({...fieldInfo, dateFormat: 'dd.MM.yyyy HH:mm'}, atts[fieldName]) as string
          if ((!val && preserveNulls) || !!val) {
            fields[fieldInfo.fieldName] = val
          }
        }
      }
      if (fields[fieldInfo.fieldName] === undefined || fields[fieldInfo.fieldName] === null) {
        fields[fieldInfo.fieldName] = ''
      }
    }
  }
  return fields
}

export const parseFeatureFields = (
  feature: Feature,
  layerInfo: IFeatureLayerInfo,
  preserveNulls?: boolean,
  customFieldParsers?: {
    [fieldName: string]: (fieldInfo: IFieldInfo, attributeValue: string | number | undefined) => string
  },
  customTypeParsers?: {
    [fieldName: string]: (fieldInfo: IFieldInfo, attributeValue: string | number | undefined) => string
  },
  skip?: {[fieldName: string]: any}
): {[label: string]: string} => {
  const fields: {[attLabel: string]: string} = {}
  let fieldInfo: IFieldInfo
  const atts = feature.getProperties()
  for (const fieldName in atts) {
    if (layerInfo.fields[fieldName] && layerInfo.fields[fieldName].visible) {
      fieldInfo = layerInfo.fields[fieldName]

      if (!preserveNulls && (atts[fieldName] === '' || atts[fieldName] === null || atts[fieldName] === undefined)) {
        continue
      } else if (skip && skip[fieldName]) {
        continue
      } else {
        if (customFieldParsers && customFieldParsers[fieldInfo.fieldName]) {
          // fields override types
          fields[fieldInfo.fieldName] = customFieldParsers[fieldInfo.fieldName](fieldInfo, atts[fieldName])
        } else if (customTypeParsers && customTypeParsers[fieldInfo.type as any]) {
          fields[fieldInfo.fieldName] = customTypeParsers[fieldInfo.type as any](fieldInfo, atts[fieldName])
        } else {
          const val = getFieldValue(fieldInfo, atts[fieldName]) as string
          if ((!val && preserveNulls) || !!val) {
            fields[fieldInfo.fieldName] = val
          }
        }
      }
      if (fields[fieldInfo.fieldName] === undefined || fields[fieldInfo.fieldName] === null) {
        fields[fieldInfo.fieldName] = ''
      }
    }
  }
  return fields
}

export const featureHasAllFields = (feature: Feature, layerInfo: IFeatureLayerInfo) => {
  let hasAllFields = true
  for (const fieldName in layerInfo.fields) {
    if (
      !feature.getProperties().hasOwnProperty(fieldName) &&
      fieldName != 'shape' &&
      fieldName != 'st_lenght(shape)' &&
      !layerInfo.fields[fieldName].forceAdd
    ) {
      hasAllFields = false
      // return;
    }
  }
  return hasAllFields
}
